import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  MLB_MY_CLUB_SLUG,
  MLB_MY_CLUB_SLUG_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { DeckEditorDialog } from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { Aside } from '@sorare/gameplay/src/components/profile/Aside';
import { ClubLayout } from '@sorare/gameplay/src/components/profile/ClubLayout';
import {
  Gallery,
  MAX_CARDS_TO_DISPLAY,
} from '@sorare/gameplay/src/components/profile/Gallery';
import { ManagerIDHeader } from '@sorare/gameplay/src/components/profile/ManagerIDHeader';
import {
  FlexGrowBlock,
  Grid,
  Main,
  NewGrid,
} from '@sorare/gameplay/src/components/profile/ui';

import {
  BaseballMyClubQuery,
  BaseballMyClubQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const BASEBALL_MY_CLUB_QUERY = gql`
  query BaseballMyClubQuery(
    $user: String!
    $firstCardsToDisplay: Int!
    $sport: Sport!
  ) {
    user(slug: $user) {
      slug
      id
      baseballUserProfile {
        id
        bannerUrl
        clubName
        shieldUrl
        socialPictureFingerprint
        highlightedDeck {
          id
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      cards(
        sport: BASEBALL
        first: $firstCardsToDisplay
        rarities: [unique, super_rare, rare, limited, common]
        inOrderOfRarities: true
      ) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
      cardCollections(startedOnly: true, first: 10, sport: BASEBALL) {
        nodes {
          id
          slug
          userCardCollection(forUserSlug: $user) {
            id
          }
        }
        totalCount
      }
      ...ManagerIDHeader_user
      ...Gallery_publicUserInfoInterface
    }
  }
  ${Gallery.fragments.deck}
  ${Gallery.fragments.anyCard}
  ${Gallery.fragments.publicUserInfoInterface}
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
` as TypedDocumentNode<BaseballMyClubQuery, BaseballMyClubQueryVariables>;

const MyClub = () => {
  const { slug } = useTypedParams<PageParams>();

  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { data, loading } = useQuery(BASEBALL_MY_CLUB_QUERY, {
    variables: {
      user: slug,
      firstCardsToDisplay: MAX_CARDS_TO_DISPLAY,
      sport: Sport.BASEBALL,
    },
  });
  const [editingListSlug, setEditingListSlug] = useState<string | null>();

  if (loading || !data?.user) {
    return <LoadingIndicator fullHeight />;
  }

  const { cards } = data.user;

  const shareProps = {
    managerProfilePath: generatePath(MLB_MY_CLUB_SLUG, { slug }),
    managerProfileSocialPictures:
      data?.user.baseballUserProfile?.managerProfileSocialPictureUrls,
    galleryPath: generatePath(MLB_MY_CLUB_SLUG_CARDS, { slug }),
    gallerySocialPictures:
      data?.user.baseballUserProfile?.gallerySocialPictureUrls,
    highlightedDeckPath: `${generatePath(MLB_MY_CLUB_SLUG_CARDS, {
      slug,
    })}?${buildFilterQuery({
      [SEARCH_PARAMS.DECK]:
        data?.user.baseballUserProfile?.highlightedDeck?.name,
    })}`,
    highlightedDeckSocialPictures:
      data?.user.baseballUserProfile?.highlightedDeck?.socialPictureUrls,
  };

  const blocks = {
    header: (
      <ManagerIDHeader
        user={data.user}
        sport={Sport.BASEBALL}
        userSportProfile={data.user.baseballUserProfile}
        {...shareProps}
      />
    ),
    gallery: (
      <>
        <Gallery
          cards={cards.nodes}
          sport={Sport.BASEBALL}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          user={data.user}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={Sport.BASEBALL}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
  };

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={data?.user.baseballUserProfile?.bannerUrl}>
        <Grid>
          {blocks.header}
          {blocks.gallery}
        </Grid>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout home bannerUrl={data?.user.baseballUserProfile?.bannerUrl}>
      <NewGrid>
        <Main>
          <FlexGrowBlock>{blocks.header}</FlexGrowBlock>
        </Main>
        <Aside>
          <FlexGrowBlock>{blocks.gallery}</FlexGrowBlock>
        </Aside>
      </NewGrid>
    </ClubLayout>
  );
};

export default MyClub;
